/**
 * Responsibility: what the operator owns, and whether it is going up.
 *
 * Laid out like the discovery board on purpose - figures across the top,
 * then one row carrying the view tabs and every control. Four pages in this
 * app show a list of cards and they should not each invent an arrangement
 * of the same three things.
 *
 * The movement figures read "not yet" rather than "0%" until the series is
 * long enough. That is not a placeholder: nothing free backfills a
 * collection's past, so the history genuinely begins the day a card is
 * added, and a zero would be a claim that the value held rather than an
 * admission that it is not known.
 *
 * Liked and watched cards used to be tabs here and are now their own pages.
 * They are not things you own, and a page called "my collection" that
 * counted them in its totals would be lying about what the totals mean.
 */

import { useMemo } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { PageContainer, PageTabs } from '@kit/components';
import { ToolbarIconButton } from '@kit/components/buttons';
import { CollectionFilterBar } from '../components/collection/CollectionFilterBar';
import { CollectionProgress } from '../components/collection/CollectionProgress';
import { FullSetPanel } from '../components/collection/FullSetPanel';
import { GroupingGrid } from '../components/shelf/GroupingGrid';
import { setGroupings, tagGroupings } from '../components/collection/groupings';
import { CollectionSortMenu } from '../components/collection/CollectionSortMenu';
import { TagManagerDialog } from '../components/collection/TagManagerDialog';
import { CollectionStats } from '../components/collection/CollectionStats';
import { CollectionTable } from '../components/collection/CollectionViews';
import {
  NO_FILTERS,
  applyFilters,
  optionsFrom,
} from '../components/collection/collectionFilters';
import type { CollectionFilters } from '../components/collection/collectionFilters';
import { DEFAULT_SORT, sortItems } from '../components/collection/collectionSort';
import type { CollectionSort } from '../components/collection/collectionSort';
import { useTags, useAddHoldingWithTags } from '../api/hooks/useTags';
import { useCollectionSets } from '../api/hooks/useSets';
import {
  useAddToCollection,
  useCollection,
  useEditHolding,
  useRemoveFromCollection,
  useRevalueCollection,
  useSaved,
} from '../api/hooks/useMarket';
import { AddHoldingDialog } from '../components/collection/AddHoldingDialog';
import { HoldingDialog } from '../components/collection/HoldingDialog';
import { ShelfGrid } from '../components/shelf/ShelfGrid';
import { ShelfToolbar } from '../components/shelf/ShelfToolbar';
import type { ShelfView } from '../components/shelf/ShelfToolbar';
import { fromHolding } from '../components/shelf/shelfCard';
import { FittedTable } from '../components/shelf/FittedTable';
import { useFillHeight } from '../hooks/useFillHeight';
import { usePageState } from '../hooks/usePageState';
import type { CollectionItem, MarketResult } from '../api/client';

export function CollectionPage() {
  const [view, setView] = usePageState<ShelfView>('collection.view', 'grid', 'durable');
  // Which part of the collection is open, independent of how it is drawn.
  const [section, setSection] = usePageState<string>(
    'collection.section',
    'collection',
    'durable',
  );
  // The grouping being looked at, which adds a fourth tab while it is set.
  const [openKind, setOpenKind] = usePageState<'tag' | 'set' | null>(
    'collection.openKind',
    null,
  );
  const [openKey, setOpenKey] = usePageState<string | null>('collection.openKey', null);
  const [expanded, setExpanded] = usePageState('collection.expanded', false, 'durable');
  // Deleting is the one action here with no undo, so it lives behind an
  // explicit edit mode rather than sitting under the pointer while
  // somebody is browsing.
  const [editing, setEditing] = usePageState('collection.editing', false);
  const [holding, setHolding] = usePageState<CollectionItem | null>('collection.open', null);
  const [adding, setAdding] = usePageState<MarketResult | null>('collection.adding', null);
  // Durable: a shelf is browsed over weeks, and having to re-narrow it
  // every visit is the whole reason the page state hook exists.
  const [filters, setFilters] = usePageState<CollectionFilters>(
    'collection.filters',
    NO_FILTERS,
    'durable',
  );
  // Durable for the same reason the filters are: the order you browse a
  // shelf in is a preference, not a per-visit decision.
  const [sort, setSort] = usePageState<CollectionSort>(
    'collection.sort',
    DEFAULT_SORT,
    'durable',
  );

  const theme = useTheme();
  const onDesktop = useMediaQuery(theme.breakpoints.up('md'));
  // Measured, not guessed: the room under the table changes when the
  // figures above it are hidden, and a constant allowance cannot know.
  const [fillRef, tableHeight] = useFillHeight();

  const collection = useCollection();
  const tagList = useTags();
  const setList = useCollectionSets();
  // Liked cards wear the same tags, so a sub-collection tile can say how
  // many of its cards are found but not yet owned.
  const saved = useSaved();
  const likedCards = useMemo(() => saved.data?.likes ?? [], [saved.data]);
  const [managingTags, setManagingTags] = usePageState('collection.tagManager', false);
  const remove = useRemoveFromCollection();
  const revalue = useRevalueCollection();
  const edit = useEditHolding();
  const add = useAddToCollection();
  const addWithTags = useAddHoldingWithTags();

  // Memoised, not `?? []`: a fresh empty array every render would make the
  // memos below recompute forever.
  const all = useMemo(() => collection.data?.items ?? [], [collection.data]);
  // Sorted after filtering, so the order is over what is actually shown.
  // Both views read this one list: a grid has no column heads to click,
  // and switching view must not silently reorder the cards.
  const items = useMemo(
    () => sortItems(applyFilters(all, filters), sort),
    [all, filters, sort],
  );
  const options = useMemo(() => optionsFrom(all), [all]);
  const tags = useMemo(() => tagList.data ?? [], [tagList.data]);
  // Null unless a sub-collection is actually being filtered on, which is
  // the only time its progress means anything.
  // **Read back out of the collection rather than used as stored.**
  // `holding` is a snapshot taken when the card was opened, so tagging it
  // would leave the dialog showing the chips as they were before the
  // click - the write succeeds and the page appears not to have noticed.
  const liveHolding = useMemo(
    () => (holding === null ? null : (all.find((item) => item.id === holding.id) ?? holding)),
    [all, holding],
  );
  // Only the set actually being filtered on; the rest of the list is for
  // the sets view, not for this panel.
  const openSet = useMemo(
    () => (setList.data ?? []).find((row) => row.label === filters.set) ?? null,
    [setList.data, filters.set],
  );
  // Built from everything owned, not from the filtered view: a grouping's
  // size does not change because a grade filter is on.
  const tagGroups = useMemo(
    () => tagGroupings(tags, all, likedCards),
    [tags, all, likedCards],
  );
  const setGroups = useMemo(
    () => setGroupings(setList.data ?? [], all),
    [setList.data, all],
  );
  const openLabel = useMemo(() => {
    if (openKind === null || openKey === null) return null;
    if (openKind === 'set') return openKey;
    return tags.find((tag) => tag.id === openKey)?.name ?? 'Sub-collection';
  }, [openKind, openKey, tags]);

  // The fourth tab exists only while a grouping is open, so the strip is
  // three entries until there is somewhere for the fourth to go back to.
  const sections = useMemo(
    () => [
      { key: 'collection', label: 'Collection' },
      { key: 'groups', label: 'Sub-collections' },
      { key: 'sets', label: 'Sets' },
      ...(openLabel !== null ? [{ key: 'open', label: openLabel }] : []),
    ],
    [openLabel],
  );

  /** Open a grouping in the fourth tab. */
  const open = (kind: 'tag' | 'set', key: string) => {
    setOpenKind(kind);
    setOpenKey(key);
    // A sub-collection is a filter over the same cards, so it narrows the
    // shelf. A set is its own listing and does not.
    setFilters({ ...filters, tag: kind === 'tag' ? key : '', set: '' });
    setSection('open');
  };

  const openTag = useMemo(
    () => tags.find((tag) => tag.id === filters.tag) ?? null,
    [tags, filters.tag],
  );
  const cards = useMemo(() => items.map(fromHolding), [items]);
  const byKey = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);

  const summary = collection.data?.summary;
  const busy = remove.isPending || revalue.isPending || edit.isPending;
  const error = collection.error ?? remove.error ?? revalue.error ?? edit.error ?? add.error;

  const empty =
    all.length === 0
      ? 'Nothing here yet. Find cards on the Search page and add them.'
      : 'No holding matches these filters. Clear them to see the rest.';

  return (
    <PageContainer
      disableBodyScroll={onDesktop}
      // The card's own first row, so the strip sits flush at the top
      // rather than under a body's worth of padding.
      tabs={<PageTabs tabs={sections.map((entry) => ({ value: entry.key, label: entry.label }))} value={section} onChange={setSection} />}
    >
      <Stack spacing={1.5} sx={{ minWidth: 0, height: '100%' }}>
        {error ? <Alert severity="error">{(error as Error).message}</Alert> : null}
        {add.isSuccess ? (
          <Alert severity="success" onClose={() => add.reset()}>
            Added to your collection.
          </Alert>
        ) : null}

        {/* Hidden while expanded: the point of expanding is that the cards
            get the container, and the figures are the largest thing in the
            way of that. */}
        {summary && !expanded ? <CollectionStats summary={summary} /> : null}

        {/* Only while a grouping is being filtered on. Against the whole
            collection it would repeat the figure already above it. */}
        {!expanded ? (
          <CollectionProgress
            items={items}
            tag={openTag}
            set={filters.set}
            setSummary={openSet}
            onOpenSet={(label) => open('set', label)}
          />
        ) : null}

        <ShelfToolbar
          view={view}
          onView={setView}
          expanded={expanded}
          onExpanded={setExpanded}
        >
          <CollectionFilterBar
            filters={filters}
            onChange={setFilters}
            options={options}
            tags={tags}
            hidden={all.length - items.length}
          />

          <CollectionSortMenu value={sort} onChange={setSort} disabled={all.length === 0} />

          <Button size="small" variant="outlined" onClick={() => setManagingTags(true)}>
            Sub-collections
          </Button>

          <Button
            size="small"
            variant={editing ? 'contained' : 'outlined'}
            startIcon={<EditIcon />}
            onClick={() => setEditing((on) => !on)}
            disabled={all.length === 0}
          >
            {editing ? 'Done' : 'Edit'}
          </Button>

          <span>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => revalue.mutate()}
              disabled={busy || all.length === 0}
            >
              Revalue
            </Button>
          </span>

        </ShelfToolbar>

        {section === 'groups' ? (
          <GroupingGrid
            groupings={tagGroups}
            emptyMessage="No sub-collections yet. Make one to group cards by a run you are completing, or by anything else."
            onOpen={(tagId) => open('tag', tagId)}
          />
        ) : section === 'sets' ? (
          <GroupingGrid
            groupings={setGroups}
            emptyMessage="No sets yet. Add a card and the set it belongs to appears here."
            onOpen={(label) => open('set', label)}
          />
        ) : section === 'open' && openKind === 'set' && openKey !== null ? (
          // A set is its own listing rather than a filter over the shelf: the
          // question it raises is which cards are missing, and those are not
          // in the collection to be filtered to.
          <FullSetPanel label={openKey} items={all} />
        ) : view === 'grid' ? (
          <ShelfGrid
            cards={cards}
            onOpen={(card) => setHolding(byKey.get(card.key) ?? null)}
            emptyMessage={empty}
            corner={
              editing
                ? (card) => (
                    <ToolbarIconButton
                      aria-label="Remove from collection"
                      disabled={busy}
                      onClick={(event) => {
                        // The tile opens the holding, so the delete has to
                        // stop the click before it gets there.
                        event.stopPropagation();
                        const item = byKey.get(card.key);
                        if (item) remove.mutate(item.id);
                      }}
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.6)',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                      }}
                    >
                      <DeleteIcon fontSize="small" sx={{ color: 'common.white' }} />
                    </ToolbarIconButton>
                  )
                : undefined
            }
          />
        ) : items.length === 0 ? (
          <Box sx={{ display: 'grid', placeItems: 'center', flex: 1, minHeight: 200, px: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {empty}
            </Typography>
          </Box>
        ) : (
          <FittedTable fillRef={fillRef}>
            <CollectionTable
              items={items}
              busy={busy}
              onRemove={(item) => remove.mutate(item.id)}
              onOpen={setHolding}
              editing={editing}
              maxHeight={onDesktop ? tableHeight : undefined}
            />
          </FittedTable>
        )}
      </Stack>

      <TagManagerDialog
        open={managingTags}
        onClose={() => setManagingTags(false)}
        tags={tags}
      />

      <HoldingDialog
        item={liveHolding}
        tags={tags}
        busy={busy}
        onClose={() => setHolding(null)}
        onSave={(itemId, changes) => {
          edit.mutate({ itemId, changes });
          setHolding(null);
        }}
      />

      <AddHoldingDialog
        card={adding}
        busy={add.isPending}
        onClose={() => setAdding(null)}
        onAdd={(body, tagIds) => {
          addWithTags.mutate({ body, tagIds });
          setAdding(null);
        }}
      />
    </PageContainer>
  );
}
